import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PatientAppointmentItem } from "@/Common/Models/Appointments/PatientAppointmentItem";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { ISelectControlOption, SelectControl } from "./SelectControl";

import styles from "./LinkedAppointmentSelect.module.scss";

interface LinkedAppointmentDropdownProps {
    appointmentList: PatientAppointmentListItem[];
    onChangeCallback?: (value: string) => void;
    value?: string;
    className?: string;
    isDisabled?: boolean;
}

// The const array of Cancelled appointment state codes': No Show, Cancelled By Personnel, Cancelled By Patient
const cancelledAppointmentStateCodes = ["NOSHOW", "CANCELED", "CANCELLED BY PATIENT"];

function LinkedAppointmentSelect(props: LinkedAppointmentDropdownProps): JSX.Element {
    const { getText } = useTranslation();
    const [selectedValue, setSelectedValue] = useState(props.value);

    function appointmentOptionControl(model: PatientAppointmentItem): JSX.Element {
        const subject = getSubjectText(model?.Subject);
        return (
            <>
                <span className="text-bold">
                    {DateHelper.formatIsoDateOnly(model?.StartDate)}
                </span>
                <span>
                    {` ${getText("AtLabel")} ${model?.StartTimeHour} `}
                </span>
                <span className={`${styles.textEllipsis}} text-bold`} title={model?.With}>
                    {model?.With}
                </span>
                {subject && (
                    <span title={subject}>
                        {` - ${subject}`}
                    </span>
                )}
            </>
        );
    }

    function getAppointmentOptionLabel(model: PatientAppointmentItem): string {
        const subject = getSubjectText(model.Subject);
        return `${DateHelper.formatIsoDateOnly(model.StartDate)} ${getText("AtLabel")} ${model.StartTimeHour} - ${subject ? `${subject} - ` : ""}${model.With}`;
    }

    function getSubjectText(subject: string): string {
        subject = subject?.trim();
        if (subject?.endsWith("-"))
            subject = subject.slice(0, -1);

        return subject ?? "";
    }

    const cancelledAppointmentIds = props.appointmentList
        ?.filter(appointment => appointment.Model && cancelledAppointmentStateCodes.includes(appointment.Model.StateCode))
        .map(appointment => appointment.Model.AppointmentId);

    const activeAppointmentsOptions: ISelectControlOption[] = useMemo(() => {
        let cancelledAppointments = cancelledAppointmentIds;
        if (cancelledAppointments.includes(props.value)) {
            const index = cancelledAppointments.indexOf(props.value);
            cancelledAppointments.splice(index, 1);
        }

        return props.appointmentList
            ?.filter(appointment => appointment.Model && !cancelledAppointments.includes(appointment.Model.AppointmentId))
            .map(appointment => ({
                value: appointment.Model.AppointmentId,
                label: appointmentOptionControl(appointment.Model),
                filterLabel: getAppointmentOptionLabel(appointment.Model)
            }));
    }, [props.appointmentList]);

    useEffect(() => {
        if (props.value !== selectedValue)
            setSelectedValue(props.value);

        return () => { };
    }, [props.value]);

    const parentClassName = `${props.className ?? ""} ${styles.appointmentSelect}`;
    return (
        <SelectControl
            value={selectedValue || null}
            optionsData={activeAppointmentsOptions || []}
            maxMenuHeight={180}
            onChangeCallback={value => props.onChangeCallback(value)}
            isEllipsis
            className={parentClassName}
            pageSize={50}
            isDisabled={props.isDisabled}
            useEmptyOption={cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote}
        />
    );
}

export { LinkedAppointmentSelect };
